<template>
    <v-card class="fill-height fluid">
        <v-toolbar dark color="primary darken-1">
            <v-toolbar-title>Calendar</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-menu offset-y>
                <template v-slot:activator="{ on }">
                    <v-btn class="ma-2" v-on="on" small outlined color="white">Legend</v-btn>
                </template>
                <v-list dense>
                    <v-list-item v-for="config in colors" :key="config.count">
                        <v-list-item-content>
                            <v-list-item-title>
                                <v-btn
                                    rounded
                                    fab
                                    :color="config.color"
                                    depressed
                                    x-small
                                    class="mr-2"
                                ></v-btn>
                                {{config.title}}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-toolbar>
        <v-card-text>
            <div class="d-flex flex-row">
                <h2 class="py-2">{{calendar1Title}}</h2>
                <v-spacer></v-spacer>
                <v-btn small icon @click="calendarOffset -= 1">
                    <v-icon>chevron_left</v-icon>
                </v-btn>
                <v-btn small icon @click="calendarOffset += 1">
                    <v-icon>chevron_right</v-icon>
                </v-btn>
            </div>
            <v-calendar :start="calendar1Start" type="month">
                <template v-slot:day-label="{ day, date }">
                    <v-btn
                        v-if="!events[date]"
                        color="transparent"
                        rounded
                        fab
                        depressed
                        small
                    >{{day}}</v-btn>
                    <v-menu v-else offset-y>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                v-if="events[date]"
                                rounded
                                fab
                                :color="getColour(date)"
                                depressed
                                small
                                v-on="on"
                            >{{day}}</v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item
                                class="mx-2"
                                v-if="events[date][preventativeMaintenance]"
                            >{{events[date][preventativeMaintenance].length}} x {{preventativeMaintenance}}</v-list-item>
                            <v-list-item
                                class="mx-2"
                                v-if="events[date][majorService]"
                            >{{events[date][majorService].length}} x {{majorService}}</v-list-item>
                            <v-list-item
                                class="mx-2"
                                v-if="events[date][defect]"
                            >{{events[date][defect].length}} x {{defect}}</v-list-item>
                            <v-list-item
                                class="mx-2"
                                v-if="events[date][dateBasedTask]"
                            >{{events[date][dateBasedTask].length}} x {{dateBasedTask}}</v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-calendar>
            <h2 class="py-2 mt-2">{{calendar2Title}}</h2>
            <v-calendar :start="calendar2Start" type="month">
                <template v-slot:day-label="{ day, date }">
                    <v-btn
                        v-if="!events[date]"
                        color="transparent"
                        rounded
                        fab
                        depressed
                        small
                    >{{day}}</v-btn>
                    <v-menu v-else offset-y>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                v-if="events[date]"
                                rounded
                                fab
                                :color="getColour(date)"
                                depressed
                                small
                                v-on="on"
                            >{{day}}</v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item
                                class="mx-2"
                                v-if="events[date][preventativeMaintenance]"
                            >{{events[date][preventativeMaintenance].length}} x {{preventativeMaintenance}}</v-list-item>
                            <v-list-item
                                class="mx-2"
                                v-if="events[date][majorService]"
                            >{{events[date][majorService].length}} x {{majorService}}</v-list-item>
                            <v-list-item
                                class="mx-2"
                                v-if="events[date][defect]"
                            >{{events[date][defect].length}} x {{defect}}</v-list-item>
                            <v-list-item
                                class="mx-2"
                                v-if="events[date][dateBasedTask]"
                            >{{events[date][dateBasedTask].length}} x {{dateBasedTask}}</v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-calendar>
        </v-card-text>
    </v-card>
</template>
<script>
import { DateFormat } from '@/plugins/date-filter';
import Services from '@/components/Services/ServicesClient.vue';
import DefectNotices from '@/components/DefectNotices/DefectNoticesClient.vue';
import DateBasedTasks from '@/components/DateBasedTasks/DateBasedTasksClient.vue';
import CalendarHelper from '@/lib/CalendarHelper';
import GarageLocations from '@/components/GarageLocations/GarageLocationsClient.vue';
import moment from 'moment';
import { TaskType, MonthEventType } from '@/models/enums';
import { GarageLocationStore } from '../../../stores/GarageLocationStore';

export default {
    mixins: [Services, DefectNotices, DateBasedTasks, GarageLocations],
    data() {
        return {
            calendarOffset: 0,
            services: [],
            dateBasedTasks: [],
            defects: [],
            preventativeMaintenance: MonthEventType.PreventativeMaintenance,
            majorService: MonthEventType.MajorService,
            defect: MonthEventType.Defect,
            dateBasedTask: MonthEventType.DateBasedTask,
            colors: []
        };
    },
    methods: {
        async init() {
            this.calendarOffset = 0;
            await this.getEvents();
            this.colors = this.makeColors();
        },
        makeColors() {
            var config = GarageLocationStore.methods.getCalendarUtilisation(GarageLocationStore.data.userGarage);
            var colors = [];

            var colorOptions = ['green', 'amber', 'red', 'purple', 'blue'];

            for (var i = 0; i < config.length; i++) {
                var me = config[i];
                if (i == config.length - 1) {
                    colors.push({ count: me, color: colorOptions[i], title: `${me}+ items` });
                    break;
                }
                var top = config[i + 1] - 1;
                if (top == me) {
                    colors.push({ count: me, color: colorOptions[i], title: `${me} item${me == 1 ? '' : 's'}` });
                } else {
                    colors.push({ count: me, color: colorOptions[i], title: `${me}-${top} items` });
                }
            }
            return colors.reverse();
        },
        async getEvents() {
            if (!this.userGarage) {
                return;
            }
            this.services = await this.getServices();
            this.defects = await this.getDefects();

            const tasks = await this.getDateBasedTasks();
            this.dateBasedTasks = tasks.filter(t => !t.isDeleted);
        },
        getColour(date) {
            if (!this.events[date]) {
                return 'transparent';
            }
            var maint = (this.events[date][this.preventativeMaintenance] || { length: 0 }).length;
            var major = (this.events[date][this.majorService] || { length: 0 }).length;
            var defect = (this.events[date][this.defect] || { length: 0 }).length;
            var dbt = (this.events[date][this.dateBasedTask] || { length: 0 }).length;

            var total = maint + major + defect + dbt;
            return this.getColorByCount(total);
        },
        getColorByCount(total) {
            for (let config of this.colors) {
                if (total >= config.count) {
                    return config.color;
                }
            }
            return 'transparent';
        }
    },
    computed: {
        events() {
            const map = {};
            this.services.filter(s => s.garageLocationId === this.userGarage).forEach(e => CalendarHelper.mapService(e, map));
            this.defects.forEach(e => CalendarHelper.mapDefect(e, map));
            this.dateBasedTasks.filter(s => s.garageLocationId === this.userGarage).forEach(e => CalendarHelper.mapDateBasedTask(e, map));
            return map;
        },
        calendar1Start() {
            return moment(new Date())
                .startOf('month')
                .add(this.calendarOffset, 'M')
                .format(DateFormat.Canonical);
        },
        calendar2Start() {
            return moment(new Date())
                .startOf('month')
                .add(1, 'M')
                .add(this.calendarOffset, 'M')
                .format(DateFormat.Canonical);
        },
        calendar1Title() {
            return moment(new Date())
                .startOf('month')
                .add(this.calendarOffset, 'M')
                .format('MMMM YYYY');
        },
        calendar2Title() {
            return moment(new Date())
                .startOf('month')
                .add(1, 'M')
                .add(this.calendarOffset, 'M')
                .format('MMMM YYYY');
        },
        from() {
            return moment(new Date())
                .startOf('month')
                .add(this.calendarOffset, 'M')
                .subtract(6, 'days')
                .format(DateFormat.Canonical);
        },
        to() {
            return moment(new Date())
                .endOf('month')
                .add(this.calendarOffset + 1, 'M')
                .add(6, 'days')
                .format(DateFormat.Canonical);
        }
    }
};
</script>
